/**
 * AuditFilters, sheet 44 callout 3 (mission 10 WP17 T099; FR-018, FR-024; A.5; US8 scenario 2; style guide §7.2,
 * §7.8, §9; contracts/audit-api.md "The filters"; research §1.27 "Sheet 44").
 *
 * The URL query is the filters' only state (style guide §7.8: filters reflect into the URL), so the 40/10 and
 * 43/3 deep links (`?type=header_param_changed`, `?type=retention_changed`) land filtered, a reload reproduces
 * the list, and the CSV request reads the same filters (AuditLog passes `auditFiltersFrom` to both):
 * - the event type, a labelled multi-select over the page's `types` (the written types only, U28) with the
 *   register 37 `auditType.*` labels, written as a repeated `type`;
 * - `from` and `to`, UTC dates, inclusive, committed on change;
 * - the free text over target and details, committed on Enter or blur, trimmed, at most 100 characters. It goes
 *   only to the URL and the reads' query strings, never an `aria-*` or `data-*` attribute.
 * Each control has its `<label for>` (FR-024). No user filter: one identity per tenant (44/3's deviation).
 * A value the API would refuse is dropped from the URL with replace, as the transmission log does.
 */
import { useEffect, type KeyboardEvent } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AUDIT_WRITTEN_TYPES, type AuditEventType, type AuditFilters as Filters } from '@/lib/api/audit'
import { copyText } from '@/lib/provisional-copy'

const DAY = /^\d{4}-\d{2}-\d{2}$/
const Q_MAX = 100

/** The filters the URL holds, and whether it holds a value the API would refuse. */
export function auditFiltersFrom(params: URLSearchParams): { filters: Filters; bad: boolean } {
  const filters: Filters = {}
  let bad = false
  const raw = params.getAll('type')
  const types = raw.filter((t): t is AuditEventType => (AUDIT_WRITTEN_TYPES as readonly string[]).includes(t))
  if (types.length !== raw.length) bad = true
  if (types.length > 0) filters.types = types
  for (const key of ['from', 'to'] as const) {
    const value = params.get(key)
    if (value === null) continue
    if (DAY.test(value)) filters[key] = value
    else bad = true
  }
  const q = params.get('q')
  if (q !== null) {
    const text = q.trim()
    if (text !== '' && [...text].length <= Q_MAX) filters.q = text
    else bad = true
  }
  return { filters, bad }
}

/** A change to the filters: an undefined member clears that filter. */
type Change = { [K in keyof Filters]?: Filters[K] | undefined }

/** The URL query for `filters`, in a fixed order: each type, then from, to and the text. */
function toSearch(filters: Change): URLSearchParams {
  const next = new URLSearchParams()
  for (const t of filters.types ?? []) next.append('type', t)
  if (filters.from !== undefined) next.set('from', filters.from)
  if (filters.to !== undefined) next.set('to', filters.to)
  if (filters.q !== undefined) next.set('q', filters.q)
  return next
}

const SELECT =
  'w-72 rounded-md border border-navy-500 bg-white px-1 py-1 text-body text-navy-950 hover:border-navy-600 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]'

export interface AuditFiltersProps {
  /** The page's `types`: every type a writer appends (U28). */
  types: readonly AuditEventType[]
}

export function AuditFilters({ types }: AuditFiltersProps) {
  const [params, setParams] = useSearchParams()
  const { filters, bad } = auditFiltersFrom(params)
  const selected = filters.types ?? []

  useEffect(() => {
    if (bad) setParams((prev) => toSearch(auditFiltersFrom(prev).filters), { replace: true })
  }, [bad, setParams])

  function update(change: Change) {
    // An undefined member clears that filter: toSearch writes only the set ones.
    setParams((prev) => toSearch({ ...auditFiltersFrom(prev).filters, ...change }))
  }

  function commitText(raw: string) {
    const text = raw.trim()
    const next = text === '' ? undefined : text
    if (next !== filters.q) update({ q: next })
  }

  function onTextKey(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault()
      commitText(event.currentTarget.value)
    }
  }

  return (
    <div role="search" className="flex flex-wrap items-end gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="audit-type">{copyText('auditFilterType')}</Label>
        <select
          id="audit-type"
          multiple
          size={5}
          className={SELECT}
          value={[...selected]}
          onChange={(event) => {
            const chosen = Array.from(event.currentTarget.selectedOptions, (o) => o.value as AuditEventType)
            update({ types: chosen.length > 0 ? chosen : undefined })
          }}
        >
          {types.map((t) => (
            <option key={t} value={t}>
              {copyText(`auditType.${t}`)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex w-44 flex-col gap-1.5">
        <Label htmlFor="audit-from">{copyText('auditFilterFrom')}</Label>
        <Input
          id="audit-from"
          type="date"
          className="font-mono"
          value={filters.from ?? ''}
          onChange={(event) => update({ from: event.target.value === '' ? undefined : event.target.value })}
        />
      </div>
      <div className="flex w-44 flex-col gap-1.5">
        <Label htmlFor="audit-to">{copyText('auditFilterTo')}</Label>
        <Input
          id="audit-to"
          type="date"
          className="font-mono"
          value={filters.to ?? ''}
          onChange={(event) => update({ to: event.target.value === '' ? undefined : event.target.value })}
        />
      </div>
      <div className="flex w-72 flex-col gap-1.5">
        <Label htmlFor="audit-q">{copyText('auditFilterText')}</Label>
        <Input
          key={filters.q ?? ''}
          id="audit-q"
          type="search"
          defaultValue={filters.q ?? ''}
          maxLength={Q_MAX}
          spellCheck={false}
          autoComplete="off"
          onKeyDown={onTextKey}
          onBlur={(event) => commitText(event.currentTarget.value)}
        />
      </div>
    </div>
  )
}
